export type BlockType = 'structure' | 'media' | 'component'

export interface EditorBlock {
  id: string
  label: string
  icon: string
  type: BlockType
  description?: string
}

export interface BlockConfigData {
  // Common
  align?: 'left' | 'center' | 'right'
  padding?: string
  backgroundColor?: string
  blockSpacing?: string // Spacing after this block (email-safe padding)
  border?: string // Border for entire block (e.g., "1px solid #ddd")
  
  // Text / Content
  title?: string
  subtitle?: string
  content?: string
  
  // Multi-column content
  col1Content?: string
  col2Content?: string
  col3Content?: string
  
  // Column widths (in pixels or percentage)
  col1Width?: string
  col2Width?: string
  col3Width?: string
  
  // Per-column styling
  col1BackgroundColor?: string
  col2BackgroundColor?: string
  col3BackgroundColor?: string
  
  col1Padding?: string
  col2Padding?: string
  col3Padding?: string
  
  col1Border?: string
  col2Border?: string
  col3Border?: string
  
  // Media
  imageUrl?: string
  imageAlt?: string
  imageWidth?: string // Width in pixels or percentage (e.g., "300px", "100%")
  imageHeight?: string // Height in pixels or auto (e.g., "200px", "auto")
  
  // Spacer
  spacerHeight?: string // Height of spacer in pixels (e.g., "40px")
  
  // Common Styling
  paddingTop?: string
  paddingBottom?: string
  paddingLeft?: string
  paddingRight?: string
  marginTop?: string
  marginBottom?: string
  borderRadius?: string
  fontSize?: string
  color?: string // Text color
  borderColor?: string
  borderWidth?: string
  borderStyle?: string
  
  // Header
  logoUrl?: string // Logo image URL
  logoWidth?: string // Logo width (e.g., "150px")
  logoHeight?: string // Logo height (e.g., "auto")
  
  // Footer
  copyrightText?: string // Copyright text (e.g., "© 2026 Company Name")
  unsubscribeText?: string // Unsubscribe link text (e.g., "Cancelar suscripción")
  
  // Social Media
  socialFb?: string
  socialIg?: string
  socialIn?: string
  socialX?: string
  socialYt?: string
  socialWa?: string
  socialSize?: string // Icon size (e.g., "24px")
  
  // Action
  buttonText?: string
  buttonUrl?: string
  buttonColor?: string
  buttonWidth?: string
  buttonPlacement?: 'above' | 'below' // For centered layout
  buttonPaddingVertical?: string
  buttonPaddingHorizontal?: string
  
  // Structure
  columns?: number
}

export interface BlockInstance {
  id: string // Unique instance ID
  blockId: string // Reference to BLOCKS_LIBRARY id
  data: BlockConfigData
}

export const BLOCKS_LIBRARY: EditorBlock[] = [
  // Structure
  { id: '1_col', label: '1 Column', icon: 'bi-square', type: 'structure', description: 'Full width section' },
  { id: '2_cols', label: '2 Columns', icon: 'bi-layout-split', type: 'structure', description: 'Two equal columns' },
  { id: '3_cols', label: '3 Columns', icon: 'bi-layout-three-columns', type: 'structure', description: 'Three equal columns' },
  { id: 'hero', label: 'Hero Section', icon: 'bi-card-image', type: 'structure', description: 'Large image with title and CTA' },
  { id: 'image_text', label: 'Image | Text', icon: 'bi-layout-sidebar', type: 'structure', description: 'Image on left, text on right' },
  
  // Header & Footer
  { id: 'header_simple', label: 'Simple Header', icon: 'bi-layout-text-window', type: 'structure', description: 'Logo and navigation header' },
  { id: 'footer_copyright', label: 'Copyright Footer', icon: 'bi-layout-text-window-reverse', type: 'structure', description: 'Copyright and unsubscribe footer' },
  { id: 'social_links', label: 'Social Links', icon: 'bi-share', type: 'structure', description: 'Social media icons and links' },
  
  // Components
  { id: 'button', label: 'Button', icon: 'bi-box-arrow-in-right', type: 'component', description: 'Call to Action button' },
  { id: 'divider', label: 'Divider', icon: 'bi-hr', type: 'component', description: 'Horizontal line' },
  { id: 'spacer', label: 'Spacer', icon: 'bi-arrow-down-up', type: 'component', description: 'Vertical space' },
  
  // Media
  { id: 'image', label: 'Image', icon: 'bi-image', type: 'media', description: 'Single image' },
]
